<template>
  <Modal
    name="UPDATE_REAL_ESTATE_DETAILS_MODAL"
    :title="$t('profile.label.updateRealEstate')"
    width="600"
    ref="modal"
    actions
  >
    <template v-slot:body>
      <v-progress-linear
        class="mt-5 mb-7"
        v-show="loading"
        indeterminate
        rounded
      />

      <div v-show="!loading">
        <v-form ref="updateRealEstateDetailsForm" lazy-validation>
          <v-text-field
            v-model="webhookUrl"
            :label="$t('profile.updateRealEstate.label.webhookUrl')"
            hide-details="auto"
            class="mb-5"
            outlined
          />

          <v-row align-content="center">
            <v-col md="7">
              <v-switch
                v-model="useCancelationLimit"
                :label="$t('profile.updateRealEstate.label.useCancelationLimit')"
                :ripple="false"
                hide-details
                inset
              />
            </v-col>
            <v-col v-show="useCancelationLimit">
              <div class="d-flex align-center mb-n3">
                <v-icon color="grey lighten-1" small>mdi-chevron-right</v-icon>
                <v-text-field
                  v-model="cancelationLimitHours"
                  :label="$t('profile.updateRealEstate.label.cancelationLimit')"
                  :rules="useCancelationLimit ? [rules.required] : []"
                  :disabled="!useCancelationLimit"
                  hide-details="auto"
                  class="ml-2"
                  outlined
                  dense
                />
              </div>
            </v-col>
          </v-row>

          <v-row align-content="center" class="mb-2">
            <v-col md="7">
              <v-switch
                v-model="useExpirationService"
                :label="$t('profile.updateRealEstate.label.useExpirationService')"
                :ripple="false"
                hide-details="auto"
                inset
              />
            </v-col>
            <v-col v-show="useExpirationService">
              <div class="d-flex align-center mb-n3">
                <v-icon color="grey lighten-1" small>mdi-chevron-right</v-icon>
                <v-text-field
                  v-model="expirationHours"
                  :label="$t('profile.updateRealEstate.label.expirationHours')"
                  :rules="useExpirationService ? [rules.required] : []"
                  :disabled="!useExpirationService"
                  hide-details="auto"
                  class="ml-2"
                  outlined
                  dense
                />
              </div>
            </v-col>
          </v-row>

          <v-switch
            v-model="allowKeyPickup"
            :label="$t('profile.updateRealEstate.label.allowKeyPickup')"
            :ripple="false"
            inset
          />

          <v-switch
            v-model="showPropertyManagerDataOnConfirmationEmail"
            :label="$t('profile.updateRealEstate.label.showPropertyManagerDataOnConfirmationEmail')"
            :ripple="false"
            inset
          />

          <v-switch
            v-model="receiveConfirmationMailCopy"
            :label="$t('profile.updateRealEstate.label.receiveConfirmationMailCopy')"
            :ripple="false"
            inset
          />

          <v-text-field
            v-model="disputeCreationDaysLimit"
            :label="$t('profile.updateRealEstate.label.disputeCreationDaysLimit')"
            :hint="$t('profile.updateRealEstate.label.disputeCreationDaysLimitHint')"
            :rules="disputeCreationDaysLimitRule"
            :min="minDisputeCreationDaysLimit"
            :max="maxDisputeCreationDaysLimit"
            type="number"
            class="mt-2"
            persistent-hint
            outlined
          />

          <v-textarea
            v-model="confirmationEmailObservations"
            :label="$t('profile.updateRealEstate.label.confirmationEmailObservations')"
            hide-details="auto"
            class="mt-5"
            rows="3"
            outlined
          />

          <div v-if="hcheckSyncEnabled && currentUserChecklistId">
            <p class="mt-5 mb-n5 font-weight-bold">{{ $t('customer.create.logo') }}</p>
            <p v-show="realEstateLogoUrl && !companyLogo" class="customerLogoWrapper text-center lighten-4 grey mt-5 pa-5">
              <img ref="realEstateLogoUrl" width="200" :src="realEstateLogoUrl" />
            </p>
            <p v-show="companyLogo" class="customerLogoWrapper text-center lighten-4 grey mt-5 pa-5">
              <img ref="companyLogo" width="200" src="#" />
            </p>
            <v-card elevation="0">
              <v-progress-linear v-if="isCompanyLogoUploading" class="mt-n1" indeterminate rounded absolute />
              <HUpload @change="handleCompanyLogo" fileTypes="image/png, image/gif, image/jpeg, image/jpg" small />
            </v-card>
          </div>
        </v-form>
      </div>
    </template>
    <template v-slot:actions>
        <v-btn
        @click="closeAllModals"
        :disabled="saving"
        elevation="0"
        class="mr-5"
        large
        text
      >
        {{ $t('profile.updateRealEstate.label.cancel') }}
      </v-btn>

      <v-btn
        @click="validate"
        :loading="saving"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('profile.updateRealEstate.label.confirm') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { required } from '@/util/errorHandling'
import { disputeCreationDaysLimitRule } from '@/util/defaultValidationRules'
import {
  MAX_DISPUTE_CREATION_DAYS_LIMIT,
  MIN_DISPUTE_CREATION_DAYS_LIMIT,
  toDisputeCreationDaysLimitBody,
} from '@/util/disputeCreationDaysLimit'

import Modal from '@/components/app/Modal'
import HostSelect from '@/components/host/HostSelect'
import HUpload from '../lib/HUpload.vue'

export default {
  name: 'UpdateRealEstateDetails',

  components: {
    Modal,
    HostSelect,
    HUpload,
  },

  computed: {
    ...mapGetters(['appointment', 'isLoading', 'isCompanyLogoUploading', 'currentRealEstate', 'realEstateLogoUrl', 'activeModal']),
  },

  methods: {
    ...mapActions(['updateRealEstateDetails', 'fetchCurrentRealEstate', 'uploadRealEstateLogo', 'downloadRealEstateLogo']),
    ...mapMutations(['closeAllModals']),

    handleCompanyLogo(file) {
      if (!this.hcheckSyncEnabled || !this.currentUserChecklistId) return

      const logoImg = this.$refs.companyLogo
      this.companyLogo = file[0]

      if (this.companyLogo) {
        const reader = new FileReader()
        reader.onload = (event) => logoImg.src = event.target.result
        reader.readAsDataURL(this.companyLogo)
      }

      this.uploadRealEstateLogo(file)
    },

    validate() {
      if (this.$refs.updateRealEstateDetailsForm.validate()) {
        this.saving = true

        const body = {
          web_hook_url: this.webhookUrl,
          use_cancelation_limit: this.useCancelationLimit,
          cancelation_limit_hours: this.cancelationLimitHours,
          use_expiration_service: this.useExpirationService,
          expiration_hours: this.expirationHours,
          allow_key_pickup: this.allowKeyPickup ?? false,
          show_property_manager_data_on_confirmation_email: this.showPropertyManagerDataOnConfirmationEmail ?? false,
          receive_confirmation_mail_copy: this.receiveConfirmationMailCopy ?? false,
          confirmation_email_observations: this.confirmationEmailObservations,
          dispute_creation_days_limit: toDisputeCreationDaysLimitBody(
            this.disputeCreationDaysLimit,
            this.loadedDisputeCreationDaysLimit
          ),
        }

        this.updateRealEstateDetails(body).finally(() => this.saving = false)
      }
    },

    getRealEstateDetails() {
      this.companyLogo = null
      this.loading = true

      this.fetchCurrentRealEstate()
        .then(() => {
          this.useCancelationLimit = this.currentRealEstate.use_cancelation_limit
          this.cancelationLimitHours = this.currentRealEstate.cancelation_limit_hours
          this.useExpirationService = this.currentRealEstate.use_expiration_service
          this.expirationHours = this.currentRealEstate.expiration_hours
          this.webhookUrl = this.currentRealEstate.web_hook_url
          this.allowKeyPickup = this.currentRealEstate.allow_key_pickup
          this.showPropertyManagerDataOnConfirmationEmail = this.currentRealEstate.show_property_manager_data_on_confirmation_email
          this.receiveConfirmationMailCopy = this.currentRealEstate.receive_confirmation_mail_copy
          this.confirmationEmailObservations = this.currentRealEstate.confirmation_email_observations
          this.hcheckSyncEnabled = this.currentRealEstate.hcheck_sync_enabled
          this.currentUserChecklistId = this.currentRealEstate.user_checklist_id
          this.disputeCreationDaysLimit = this.currentRealEstate.dispute_creation_days_limit ?? null
          this.loadedDisputeCreationDaysLimit = this.disputeCreationDaysLimit
        })
        .then(() => {
          if (this.hcheckSyncEnabled && this.currentUserChecklistId) {
            this.downloadRealEstateLogo()
          }
        })
        .finally(() => this.loading = false)
    },
  },

  data() {
    return {
      loading: false,
      saving: false,

      webhookUrl: '',
      useCancelationLimit: false,
      cancelationLimitHours: 1,
      useExpirationService: false,
      allowKeyPickup: true,
      showPropertyManagerDataOnConfirmationEmail: true,
      receiveConfirmationMailCopy: false,
      confirmationEmailObservations: '',
      hcheckSyncEnabled: false,
      currentUserChecklistId: null,
      expirationHours: 1,
      disputeCreationDaysLimit: null,
      loadedDisputeCreationDaysLimit: null,
      disputeCreationDaysLimitRule,
      minDisputeCreationDaysLimit: MIN_DISPUTE_CREATION_DAYS_LIMIT,
      maxDisputeCreationDaysLimit: MAX_DISPUTE_CREATION_DAYS_LIMIT,
      rules: {
        required,
      },

      companyLogo: null,
    }
  },

  mounted() {
    this.$watch(() => this.$refs.modal.dialog, (isModalOpen) => {
      if (isModalOpen) this.getRealEstateDetails()
    })
  }
}
</script>
